import {
  getExpenseAmounts,
  getExpensePayout,
  getExpenseTotal,
  sumMoney,
} from "./finance";
import { getCollectionPresentation } from "./collections/presentation";
import {
  getKnownPlannedAmount,
  sortActivePlannedExpenses,
} from "./plannedExpenses";

export const EXPENSE_REPORT_STATUS = {
  "Zakupione / Nie wypłacone": { label: "Do zwrotu", tone: "orange" },
  Wypłacone: { label: "Rozliczone", tone: "green" },
  Planowane: { label: "Planowane", tone: "violet" },
  Anulowane: { label: "Anulowane", tone: "muted" },
};

export function getExpenseReportStatus(status) {
  return EXPENSE_REPORT_STATUS[status] ?? {
    label: status || "—",
    tone: "muted",
  };
}

export function getExpenseChildCount(expense = {}) {
  const count = Number(expense.student_count_snapshot);
  return Number.isSafeInteger(count) && count > 0 ? count : null;
}

export function getExpenseCostPerChild(expense = {}) {
  if (!getExpenseChildCount(expense)) return null;
  const value = getExpenseAmounts(expense).costPerChild;
  return Number.isFinite(Number(value)) ? Number(value) : null;
}

export function isRealizedExpense(expense = {}) {
  return (
    expense.status === "Wypłacone" ||
    expense.status === "Zakupione / Nie wypłacone"
  );
}

export function buildExpenseCategoryBreakdown(expenses = []) {
  const realized = expenses.filter(isRealizedExpense);
  const realizedTotal = sumMoney(realized.map(getExpenseTotal));
  const totals = new Map();

  realized.forEach((expense) => {
    const label = String(expense.occasion || "Bez kategorii").trim() || "Bez kategorii";
    totals.set(label, sumMoney([totals.get(label) ?? 0, getExpenseTotal(expense)]));
  });

  return Array.from(totals, ([name, amount]) => ({
    name,
    amount,
    percentage: realizedTotal > 0 ? (amount / realizedTotal) * 100 : 0,
  })).sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name, "pl"));
}

export function getExpenseDocumentStats(expenses = []) {
  const attached = expenses.filter((expense) => Boolean(expense.receipt_link)).length;
  return {
    attached,
    missing: Math.max(0, expenses.length - attached),
    total: expenses.length,
  };
}

export function getActiveCollection(collections = []) {
  return (
    collections.find(
      (collection) =>
        collection?.status === "active" || collection?.status === "aktywna"
    ) ?? null
  );
}

export function buildCollectionSummary(collection) {
  if (!collection) return { active: false };

  const presentation = getCollectionPresentation(collection);
  return {
    active: true,
    name: collection.name || "Aktywna zbiórka",
    currentAmount: presentation.currentAmount,
    targetAmount: presentation.targetAmount ?? 0,
    contributionMode: presentation.contributionMode,
    isFlexibleRange: presentation.isFlexibleRange,
    isVoluntary: presentation.isVoluntary,
    minContribution: presentation.minContribution,
    maxContribution: presentation.maxContribution,
    affectsClassBalance: presentation.affectsClassBalance,
    startDate: collection.start_date || null,
    deadline: collection.end_date || collection.deadline || collection.due_date || null,
    progress: presentation.progress,
  };
}

export function buildExpenseReportRows(expenses = []) {
  return expenses.map((expense, index) => ({
    index: index + 1,
    occasion: expense.occasion || "—",
    purchaseDate: expense.purchase_date || null,
    itemOrService: expense.item_or_service || "—",
    quantity:
      expense.quantity === null || expense.quantity === undefined || expense.quantity === ""
        ? null
        : expense.quantity,
    childCount: getExpenseChildCount(expense),
    total: getExpenseTotal(expense),
    costPerChild: getExpenseCostPerChild(expense),
    status: getExpenseReportStatus(expense.status),
    documentUrl: expense.receipt_link || null,
  }));
}

export function buildExpenseReportSummary(expenses = [], studentCount = null) {
  const realizedExpenses = expenses.filter(isRealizedExpense);
  const realized = sumMoney(realizedExpenses.map(getExpenseTotal));
  const toReturn = sumMoney(
    expenses
      .filter((expense) => expense.status === "Zakupione / Nie wypłacone")
      .map(getExpenseTotal)
  );
  const paidOut = sumMoney(
    expenses
      .filter((expense) => expense.status === "Wypłacone")
      .map(getExpensePayout)
  );
  const totalValue = sumMoney(expenses.map(getExpenseTotal));
  const validStudentCount = Number(studentCount);
  const averagePerStudent =
    Number.isSafeInteger(validStudentCount) && validStudentCount > 0
      ? realized / validStudentCount
      : null;

  return {
    count: expenses.length,
    realizedCount: realizedExpenses.length,
    totalValue,
    realized,
    toReturn,
    paidOut,
    averagePerStudent,
    studentCount:
      Number.isSafeInteger(validStudentCount) && validStudentCount > 0
        ? validStudentCount
        : null,
    documents: getExpenseDocumentStats(expenses),
  };
}

export function buildPlannedExpenseReportSummary(plannedExpenses = []) {
  const active = sortActivePlannedExpenses(plannedExpenses);
  const items = active.map((expense) => ({
    id: expense.id ?? null,
    occasion: expense.occasion || expense.name || "Planowany wydatek",
    description: expense.description || null,
    plannedDate: expense.planned_date || null,
    amount: getKnownPlannedAmount(expense),
  }));
  const knownTotal = sumMoney(
    items.map((item) => item.amount ?? 0)
  );

  return {
    activeCount: items.length,
    items: items.slice(0, 2),
    overflowCount: Math.max(0, items.length - 2),
    knownTotal,
    hasUnknownAmount: items.some((item) => item.amount === null),
  };
}

export function buildReportIdentifier(className, schoolYear, date = new Date()) {
  const compact = (value, fallback) =>
    String(value || fallback)
      .normalize("NFKD")
      .replace(/[^A-Za-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toUpperCase();
  const datePart = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("");
  return `${compact(className, "KLASA")}/${compact(schoolYear, "ROK")}/${datePart}`;
}
